import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Label, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { roundTwoDecimal } from '../../calculation/util';
import { EResultCalculationType, EUnitOfMeasure } from '../../shared/enum';
import { IFsPreAndPost } from '../../shared/iResult';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { setResultCalculationImage } from '../../store/Image/imageSlice';
import { selectProject } from '../../store/project/projectSlice';
import { selectResult } from '../../store/result/resultSlice';
import { getImageSourceBase64 } from './Utils';
import { EUnitOfMeasureImperialLabel, EUnitOfMeasureMetricLabel } from '../../conversion/enum';

const color = {
    text: 'black',
    preFsStatic: '#068837',
    postFsStatic: '#068837',
    fsStaticMin: '#068837',
    preFsSeismic: '#FBBC33',
    postFsSeismic: '#FBBC33',
    fsSeismicMin: '#FBBC33',
};

interface IResultCalculationProps {
    type: EResultCalculationType;
    withExport?: boolean;
}

export default (props: IResultCalculationProps) => {
    const chartRef = React.useRef(null);

    const { t } = useTranslation();

    const result = useAppSelector(selectResult);
    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();

    const fSPreAndPost: IFsPreAndPost[] = result.data!.phaseTwoOutputCalculation.fsTablesWithResults.fsPreAndPost;

    React.useEffect(() => {
        if (props.withExport) {
            setTimeout(() => {
                const svgElement = (chartRef.current! as any).container.querySelector('svg');
                let imgSource = getImageSourceBase64(svgElement);
                switch (props.type) {
                    case EResultCalculationType.Axis:
                        dispatch(setResultCalculationImage({ axis: imgSource }));
                        break;
                    case EResultCalculationType.PreFsStatic:
                        dispatch(setResultCalculationImage({ preFsStatic: imgSource }));
                        break;
                    case EResultCalculationType.PostFsStatic:
                        dispatch(setResultCalculationImage({ postFsStatic: imgSource }));
                        break;
                    case EResultCalculationType.PreFsSeismic:
                        dispatch(setResultCalculationImage({ preFsSeismic: imgSource }));
                        break;
                    case EResultCalculationType.PostFsSeismic:
                        dispatch(setResultCalculationImage({ postFsSeismic: imgSource }));
                        break;
                }
            }, 2000);
        }
    }, []);

    return (
        <Stack direction={'row'} height={'100%'} spacing={20}>
            <Stack flex={1}>
                <Typography style={{ width: 300, textAlign: 'end', fontSize: '0.8rem' }}>{t('campaignPlan')}</Typography>
                <ResponsiveContainer width={300} height={'100%'}>
                    <LineChart layout='vertical' data={fSPreAndPost} ref={chartRef}>
                        <CartesianGrid strokeDasharray='3 3' strokeWidth={0.5} />
                        <XAxis
                            strokeWidth={props.type == EResultCalculationType.Axis ? 1 : 0}
                            tick={props.type == EResultCalculationType.Axis}
                            type='number'
                            domain={[0, (dataMax: number) => (dataMax > 5 ? 5 : dataMax) + 0.5]}
                            allowDataOverflow
                            tickFormatter={value => roundTwoDecimal(value).toString()}
                            orientation='top'
                            style={{
                                fontSize: '0.7rem',
                                color: color.text,
                                fontFamily: 'Roboto, sans-serif',
                            }}>
                            {props.type == EResultCalculationType.Axis && <Label value={t('fs')} fontFamily='Roboto, sans-serif' offset={0} fontSize={'0.7rem'} position='insideTopRight' />}
                        </XAxis>
                        <YAxis
                            strokeWidth={props.type == EResultCalculationType.Axis ? 1 : 0}
                            tick={props.type == EResultCalculationType.Axis}
                            dataKey='z'
                            interval={0}
                            ticks={Array.from({ length: project.data?.slope?.impermeableLayerDepth! + 1 }, (_, i) => i)}
                            domain={[0, project.data?.slope?.impermeableLayerDepth!]}
                            tickFormatter={value => roundTwoDecimal(value).toString()}
                            type='number'
                            style={{
                                fontSize: '0.7rem',
                                color: color.text,
                                fontFamily: 'Roboto, sans-serif',
                            }}>
                            {props.type == EResultCalculationType.Axis && (
                                <Label
                                    value={t('z', {
                                        unitOfMeasure: project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot,
                                    })}
                                    angle={-90}
                                    fontSize={'0.7rem'}
                                    fontFamily='Roboto, sans-serif'
                                    offset={20}
                                    position='insideBottom'
                                />
                            )}
                        </YAxis>
                        <Line
                            dataKey='preFsStatic'
                            strokeWidth={props.type === EResultCalculationType.PreFsStatic ? 3 : 0}
                            stroke={color.preFsStatic}
                            dot={false}
                            strokeDasharray={'3 3'}
                            isAnimationActive={false}
                        />
                        <Line dataKey='postFsStatic' strokeWidth={props.type === EResultCalculationType.PostFsStatic ? 3 : 0} stroke={color.postFsStatic} dot={false} isAnimationActive={false} />
                        <ReferenceLine
                            x={project.data?.load?.minFsStaticConditions}
                            stroke={color.fsStaticMin}
                            strokeDasharray='2 4 10 2'
                            strokeWidth={props.type === EResultCalculationType.PreFsStatic || props.type === EResultCalculationType.PostFsStatic ? 1.5 : 0}
                        />
                        {project.data?.load?.hasSeismicLoads && (
                            <>
                                <ReferenceLine
                                    x={project.data?.load?.minFsSeismicConditions}
                                    stroke={color.fsSeismicMin}
                                    strokeDasharray='2 4 10 2'
                                    strokeWidth={props.type === EResultCalculationType.PreFsSeismic || props.type === EResultCalculationType.PostFsSeismic ? 1.5 : 0}
                                />
                                <Line
                                    dataKey='preFsSeismic'
                                    strokeWidth={props.type === EResultCalculationType.PreFsSeismic ? 3 : 0}
                                    stroke={color.preFsSeismic}
                                    dot={false}
                                    strokeDasharray={'3 3'}
                                    isAnimationActive={false}
                                />
                                <Line
                                    dataKey='postFsSeismic'
                                    strokeWidth={props.type === EResultCalculationType.PostFsSeismic ? 3 : 0}
                                    stroke={color.postFsSeismic}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            </>
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </Stack>
        </Stack>
    );
};
